import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Chip, Tooltip } from "@material-ui/core";
import {
  Description,
  CloudUpload,
  CloudDone,
  CloudOff,
} from "@material-ui/icons";
import { whiteColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  inputBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    maxWidth: (props) => (props.isHorizontalSection ? "320px" : "none"),
    minHeight: "56px", // Slightly reduced for compactness
    border: `1px solid #e0e0e0`,
    backgroundColor: (props) => (props.isNotApplicable ? "#fafafa" : "#f9f9f9"),
    borderRadius: "8px",
    cursor: (props) => (props.isNotApplicable ? "not-allowed" : "pointer"),
    transition: "all 0.2s ease-in-out",
    padding: theme.spacing(1.25), // Slightly reduced padding
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    position: "relative",
    opacity: (props) => (props.isNotApplicable ? 0.6 : 1),
    "&:hover": {
      transform: (props) => (props.isNotApplicable ? "none" : "scale(1.02)"),
      backgroundColor: (props) =>
        props.isNotApplicable ? "#fafafa" : "#ffffff",
      boxShadow: (props) =>
        props.isNotApplicable
          ? "0 1px 3px rgba(0,0,0,0.06)"
          : "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: "#d0d0d0",
      borderLeft: (props) =>
        props.isNotApplicable
          ? `1px solid #e0e0e0`
          : `4px solid ${props.clientColor || "#1976d2"}`,
    },
  },
  contentArea: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  selected: {
    backgroundColor: "#ffffff",
    borderColor: "#d0d0d0",
    borderLeft: (props) => `4px solid ${props.clientColor || "#1976d2"}`,
    boxShadow: (props) => `0 2px 8px ${props.clientColor || "#1976d2"}22`,
    "&:hover": {
      backgroundColor: "#fafafa",
      borderLeftWidth: "5px",
      boxShadow: (props) => `0 4px 12px ${props.clientColor || "#1976d2"}33`,
    },
  },
  iconContainer: {
    position: "relative",
    width: "40px",
    height: "40px",
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  fileIcon: {
    height: "36px",
    width: "36px",
    opacity: "0.3",
    color: "#616161",
    transition: "all 0.2s ease-in-out",
  },
  statusBadgesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  statusBadge: {
    width: "22px",
    height: "22px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  statusIcon: {
    fontSize: "0.8rem",
    color: whiteColor,
  },
  // Status colors following ModelingInputs pattern
  statusSuccess: {
    backgroundColor: "#4caf50",
  },
  statusError: {
    backgroundColor: "#f44336",
  },
  statusWaiting: {
    backgroundColor: "#ff9800",
  },
  statusInactive: {
    backgroundColor: "#9e9e9e",
  },
  tableDescription: {
    flexGrow: 1,
    fontSize: "0.75rem",
    lineHeight: "1.25",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    "-webkit-line-clamp": 2, // Reduced to 2 lines for compactness
    "-webkit-box-orient": "vertical",
    color: "#3C4858",
    fontWeight: 400,
  },
  notApplicableChip: {
    height: "22px",
    fontSize: "0.6rem",
    fontWeight: 400,
    backgroundColor: "#e0e0e0",
    color: "#757575",
    border: "1px solid #d0d0d0",
  },
}));

// Upload Status Badge
const getUploadStatusBadge = (status, classes) => {
  let badgeClass = "";
  let icon = null;

  switch (status) {
    case "ready":
    case "uploaded":
      badgeClass = classes.statusSuccess;
      icon = <CloudDone className={classes.statusIcon} />;
      break;
    case "error":
      badgeClass = classes.statusError;
      icon = <CloudOff className={classes.statusIcon} />;
      break;
    case "empty":
    case "waiting":
      badgeClass = classes.statusInactive;
      icon = <CloudUpload className={classes.statusIcon} />;
      break;
    default:
      badgeClass = classes.statusInactive;
      icon = <CloudUpload className={classes.statusIcon} />;
  }

  return <div className={`${classes.statusBadge} ${badgeClass}`}>{icon}</div>;
};

export default function CardInput({
  item,
  isSelected,
  onClick,
  clientColor,
  isHorizontalSection = false,
  isNotApplicable = false,
}) {
  const classes = useStyles({
    clientColor,
    isHorizontalSection,
    isNotApplicable,
  });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      // Check if text is truncated by comparing scrollHeight with clientHeight
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [item.desc]);

  return (
    <Tooltip title={isTruncated ? item.desc : ""} placement="top" arrow>
      <div
        className={`${classes.inputBox} ${isSelected ? classes.selected : ""}`}
        onClick={isNotApplicable ? undefined : onClick}
      >
        <div className={classes.contentArea}>
          <div className={classes.iconContainer}>
            {/* Main file icon */}
            <Description className={classes.fileIcon} />
          </div>
          <Typography ref={textRef} className={classes.tableDescription}>
            {item.desc}
          </Typography>
        </div>

        {/* Status badges container - now on the right */}
        <div className={classes.statusBadgesContainer}>
          {/* Show "Not Applicable" chip or Upload status badge */}
          {isNotApplicable ? (
            <Chip
              label={t("not_applicable")}
              size="small"
              className={classes.notApplicableChip}
            />
          ) : (
            getUploadStatusBadge(item.ready || "empty", classes)
          )}
        </div>
      </div>
    </Tooltip>
  );
}

CardInput.propTypes = {
  item: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  isHorizontalSection: PropTypes.bool,
  isNotApplicable: PropTypes.bool,
};
